/**
 * The call window's side panel, for meetings: who is on the call, the chat,
 * and the call's settings.
 *
 *  - People: hosts and co-hosts marked; raised hands numbered in the order
 *    they went up; each person's microphone, camera and screen. A host or
 *    co-host also sees the lobby (Admit, Deny), and can mute one person or
 *    everybody, remove somebody, and — the host only — make a co-host or hand
 *    the host over. Muting asks the other device to mute itself; nobody can
 *    be held muted, and the panel does not pretend otherwise.
 *  - Chat: one line at a time, kept with the call — a late joiner reads what
 *    was said before they came in.
 *  - Settings: who can join, locked or not, the call's title. Everyone can
 *    read them; hosts and co-hosts change them.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { handQueue, lobby, moderates, roleOf, type Call, type JoinRule, type Participant } from "../../domain/calls";
import { locale, t, tn } from "../../i18n";
import type { CallPanel, CallsApi } from "./CallsProvider";
import { CALL_ICONS, CallIcon, Face } from "./CallWindow";
import { JoinRuleEditor } from "./JoinRuleEditor";
import s from "./CallWindow.module.css";

export function SidePanel({ calls, call, panel }: { calls: CallsApi; call: Call; panel: CallPanel }) {
  const me = calls.state.me;
  const waiting = moderates(call, me) ? lobby(call).length : 0;
  const unread = calls.state.messages.filter((m) => m.id > calls.state.chatRead && m.from !== me).length;
  const tabs: { id: CallPanel; label: string; badge?: number }[] = [
    { id: "people", label: t("People"), badge: waiting || undefined },
    { id: "chat", label: t("Chat"), badge: panel === "chat" ? undefined : unread || undefined },
    { id: "settings", label: t("Settings") },
  ];
  return (
    <aside className={s.side} data-testid="call-side" aria-label={t("Meeting panel")}>
      <div className={s.sideTabs} role="tablist">
        {tabs.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={panel === x.id}
            className={s.sideTab}
            onClick={() => calls.openPanel(x.id)}
            data-testid={`side-tab-${x.id}`}
          >
            {x.label}
            {x.badge ? <span className={s.countBadge}>{x.badge}</span> : null}
          </button>
        ))}
        <button
          type="button"
          className={s.headBtn}
          onClick={() => calls.openPanel(null)}
          aria-label={t("Close the panel")}
          title={t("Close the panel")}
        >
          <CallIcon d={CALL_ICONS.close} size={14} />
        </button>
      </div>
      {panel === "people" && <People calls={calls} call={call} />}
      {panel === "chat" && <Chat calls={calls} call={call} />}
      {panel === "settings" && <Settings calls={calls} call={call} />}
    </aside>
  );
}

/* ── people ───────────────────────────────────────────────────────────── */

const ROLE_LABEL = { host: "Host", cohost: "Co-host", member: "" } as const;

function People({ calls, call }: { calls: CallsApi; call: Call }) {
  const { engine, state } = calls;
  const me = state.me;
  const mod = moderates(call, me);
  const iAmHost = roleOf(call, me) === "host";
  const queue = handQueue(call);
  const order = new Map(queue.map((p, i) => [p.principalId, i + 1]));
  const waiting = lobby(call);
  const on = call.participants
    .filter((p) => p.state === "joined" || p.state === "ringing")
    .sort(
      (a, b) =>
        Number(b.principalId === me) - Number(a.principalId === me) ||
        rank(a, call) - rank(b, call) ||
        Number(a.state === "ringing") - Number(b.state === "ringing") ||
        a.name.localeCompare(b.name),
    );
  const [open, setOpen] = useState<string | null>(null);
  const busy = state.busy;

  return (
    <div className={s.sideBody} data-testid="call-people-panel">
      {mod && (
        <div className={s.sideBar}>
          <button
            type="button"
            className={s.pill}
            disabled={busy}
            onClick={() => void engine.mute("all")}
            data-testid="mute-all"
          >
            <CallIcon d={CALL_ICONS.micOff} size={14} /> {t("Mute all")}
          </button>
        </div>
      )}

      {mod && waiting.length > 0 && (
        <section className={s.sideSection} data-testid="call-lobby">
          <h4>{tn(waiting.length, "{n} waiting to join", "{n} waiting to join")}</h4>
          {waiting.map((p) => (
            <div key={p.principalId} className={s.personRow} data-testid={`lobby-${p.principalId}`}>
              <Face name={p.name} employeeId={p.employeeId} hasPhoto={p.hasPhoto} size={30} />
              <span className={s.personName}>{p.name}</span>
              <button
                type="button"
                className={`${s.pill} ${s.pillGreen}`}
                disabled={busy}
                onClick={() => void engine.admit(p.principalId, true)}
                data-testid={`admit-${p.principalId}`}
              >
                {t("Admit")}
              </button>
              <button
                type="button"
                className={s.pill}
                disabled={busy}
                onClick={() => void engine.admit(p.principalId, false)}
                data-testid={`deny-${p.principalId}`}
              >
                {t("Deny")}
              </button>
            </div>
          ))}
        </section>
      )}

      {queue.length > 0 && (
        <section className={s.sideSection} data-testid="hand-queue">
          <h4>{t("Hands raised")}</h4>
          <ol className={s.handList}>
            {queue.map((p) => (
              <li key={p.principalId}>
                <span className={s.handIcon}>✋</span> {p.principalId === me ? t("You") : p.name}
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className={s.sideSection}>
        <h4>{tn(on.length, "{n} on the call", "{n} on the call")}</h4>
        {on.map((p) => {
          const self = p.principalId === me;
          const role = roleOf(call, p.principalId);
          const canAct = mod && !self && p.state === "joined" && (iAmHost || role === "member");
          const hand = order.get(p.principalId);
          return (
            <div key={p.principalId} className={s.personBlock} data-testid={`person-row-${p.principalId}`}>
              <div className={s.personRow}>
                <Face name={p.name} employeeId={p.employeeId} hasPhoto={p.hasPhoto} size={30} />
                <span className={s.personName}>
                  {self ? t("{name} (you)", { name: p.name || t("You") }) : p.name}
                  {role !== "member" && <em className={s.roleTag}>{t(ROLE_LABEL[role])}</em>}
                  {p.state === "ringing" && <em className={s.dimTag}>{t("Ringing…")}</em>}
                </span>
                {hand && (
                  <span
                    className={s.handBadge}
                    title={t("Hand raised")}
                    aria-label={t("Hand raised, {n} in line", { n: hand })}
                  >
                    ✋{hand}
                  </span>
                )}
                {p.sharing && (
                  <span className={s.stateIcon} title={t("Sharing their screen")}>
                    <CallIcon d={CALL_ICONS.screen} size={15} />
                  </span>
                )}
                <span className={s.stateIcon} title={p.audioOn ? t("Microphone on") : t("Muted")}>
                  <CallIcon d={p.audioOn ? CALL_ICONS.mic : CALL_ICONS.micOff} size={15} />
                </span>
                <span className={s.stateIcon} title={p.videoOn ? t("Camera on") : t("Camera off")}>
                  <CallIcon d={p.videoOn ? CALL_ICONS.cam : CALL_ICONS.camOff} size={15} />
                </span>
                {canAct && (
                  <button
                    type="button"
                    className={s.headBtn}
                    aria-label={t("Actions for {name}", { name: p.name })}
                    aria-expanded={open === p.principalId}
                    onClick={() => setOpen(open === p.principalId ? null : p.principalId)}
                    data-testid={`actions-${p.principalId}`}
                  >
                    ⋯
                  </button>
                )}
              </div>
              {canAct && open === p.principalId && (
                <div className={s.personActions}>
                  {p.audioOn && (
                    <button
                      type="button"
                      className={s.pill}
                      disabled={busy}
                      onClick={() => void engine.mute(p.principalId)}
                      data-testid={`mute-${p.principalId}`}
                    >
                      {t("Mute")}
                    </button>
                  )}
                  {iAmHost && (
                    <button
                      type="button"
                      className={s.pill}
                      disabled={busy}
                      onClick={() => void engine.setRole(p.principalId, role === "cohost" ? "member" : "cohost")}
                      data-testid={`cohost-${p.principalId}`}
                    >
                      {role === "cohost" ? t("Remove co-host") : t("Make co-host")}
                    </button>
                  )}
                  {iAmHost && (
                    <button
                      type="button"
                      className={s.pill}
                      disabled={busy}
                      onClick={() => void engine.setRole(p.principalId, "host")}
                      data-testid={`host-${p.principalId}`}
                    >
                      {t("Make host")}
                    </button>
                  )}
                  <button
                    type="button"
                    className={`${s.pill} ${s.pillRed}`}
                    disabled={busy}
                    onClick={() => {
                      setOpen(null);
                      void engine.remove(p.principalId);
                    }}
                    data-testid={`remove-${p.principalId}`}
                  >
                    {t("Remove from call")}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </section>
      {mod && (
        <p className={s.sideNote}>
          {t(
            "Mute asks their device to mute; they can unmute themselves. Removing someone also stops them joining again.",
          )}
        </p>
      )}
    </div>
  );
}

function rank(p: Participant, call: Call): number {
  const r = roleOf(call, p.principalId);
  return r === "host" ? 0 : r === "cohost" ? 1 : 2;
}

/* ── chat ─────────────────────────────────────────────────────────────── */

function Chat({ calls, call }: { calls: CallsApi; call: Call }) {
  const { engine, state } = calls;
  const me = state.me;
  const [text, setText] = useState("");
  const list = useRef<HTMLDivElement | null>(null);
  const count = state.messages.length;
  useEffect(() => {
    engine.markChatRead();
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, engine]);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (await engine.sendMessage(text)) setText("");
  };
  return (
    <div className={`${s.sideBody} ${s.chat}`} data-testid="call-chat">
      <div className={s.chatList} ref={list} aria-live="polite">
        {state.messages.length === 0 ? (
          <p className={s.sideNote}>{t("No messages yet. What is written here stays with the call.")}</p>
        ) : (
          state.messages.map((m) => {
            const mine = m.from === me;
            return (
              <div key={m.id} className={`${s.msg} ${mine ? s.msgMine : ""}`} data-testid={`msg-${m.id}`}>
                {!mine && <b>{m.name || call.participants.find((p) => p.principalId === m.from)?.name}</b>}
                <span>{m.text}</span>
                <time>
                  {m.createdAt
                    ? new Date(m.createdAt).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" })
                    : ""}
                </time>
              </div>
            );
          })
        )}
      </div>
      <form className={s.chatForm} onSubmit={(e) => void send(e)}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={2000}
          placeholder={t("Write to everyone on the call")}
          aria-label={t("Message")}
          data-testid="chat-input"
        />
        <button
          type="submit"
          className={`${s.pill} ${s.pillGreen}`}
          disabled={!text.trim() || state.busy}
          data-testid="chat-send"
        >
          {t("Send")}
        </button>
      </form>
    </div>
  );
}

/* ── settings ─────────────────────────────────────────────────────────── */

function Settings({ calls, call }: { calls: CallsApi; call: Call }) {
  const { engine, state } = calls;
  const mod = moderates(call, state.me);
  const saved: JoinRule = call.join ?? { mode: "invited", allow: [] };
  const [join, setJoin] = useState<JoinRule | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const shown = join ?? saved;
  const dirty = join != null || (title != null && title !== (call.title ?? ""));
  const save = async () => {
    const ok = await engine.settings({
      ...(join ? { join } : {}),
      ...(title != null ? { title } : {}),
    });
    if (ok) {
      setJoin(null);
      setTitle(null);
    }
  };
  return (
    <div className={s.sideBody} data-testid="call-settings">
      {!mod && <p className={s.sideNote}>{t("Only the host and co-hosts can change these.")}</p>}
      <section className={s.sideSection}>
        <h4>{t("Title")}</h4>
        <input
          className={s.sideInput}
          value={title ?? call.title ?? ""}
          onChange={(e) => setTitle(e.target.value)}
          disabled={!mod}
          maxLength={120}
          placeholder={t("A name for this call")}
          aria-label={t("Title")}
        />
      </section>
      <section className={s.sideSection}>
        <h4>{t("Who can join")}</h4>
        <JoinRuleEditor value={shown} onChange={setJoin} disabled={!mod} testId="call-join" />
      </section>
      <section className={s.sideSection}>
        <label className={s.lockRow}>
          <input
            type="checkbox"
            checked={!!call.locked}
            disabled={!mod || state.busy}
            onChange={(e) => void engine.settings({ locked: e.target.checked })}
            data-testid="call-lock"
          />
          <span>
            <b>{t("Lock the call")}</b>
            <span className={s.sideNote}>{t("Nobody new can join or ask to — only the people already invited.")}</span>
          </span>
        </label>
      </section>
      {mod && (
        <button
          type="button"
          className={`${s.pill} ${s.pillGreen}`}
          disabled={!dirty || state.busy}
          onClick={() => void save()}
          data-testid="call-settings-save"
        >
          {t("Save the settings")}
        </button>
      )}
    </div>
  );
}
